import React from 'react';
import adminStorage from './adminStorage';

// Access Control Utility for Admin Functions

/**
 * Check if the current admin user has the required access level
 * @param {string} requiredLevel - Required access level ('1' for super admin, '2' for regular admin, etc.)
 * @returns {object} - { hasAccess: boolean, adminData: object|null, error: string|null }
 */
export const checkAdminAccess = (requiredLevel = '1') => {
  try {
    const adminData = adminStorage.getItem('data');
    
    if (!adminData) {
      return {
        hasAccess: false,
        adminData: null,
        error: 'No admin session found. Please log in.'
      };
    }
    
    const admin = JSON.parse(adminData);
    
    if (!admin.access) {
      return {
        hasAccess: false,
        adminData: admin,
        error: 'Access level not defined for this admin account.'
      };
    }
    
    // Check if user has required access level
    // Level '1' is highest (super admin), '2' is regular admin, etc.
    const userLevel = parseInt(admin.access);
    const requiredLevelNum = parseInt(requiredLevel);
    
    if (userLevel > requiredLevelNum) {
      return {
        hasAccess: false,
        adminData: admin,
        error: `Insufficient privileges. Required level: ${requiredLevel}, Your level: ${admin.access}`
      };
    }
    
    return {
      hasAccess: true,
      adminData: admin,
      error: null
    };
    
  } catch (error) {
    return {
      hasAccess: false,
      adminData: null,
      error: 'Error parsing admin session data.'
    };
  }
};

/**
 * Get admin access levels with descriptions
 * @returns {object} - Object mapping access levels to descriptions
 */
export const getAccessLevels = () => {
  return {
    '1': 'Super Admin (Full Access)',
    '2': 'Admin (Limited Access)',
    '3': 'Moderator (Basic Access)',
    '4': 'Viewer (Read Only)'
  };
};

/**
 * Check if user can access database management features
 * @returns {object} - Access check result
 */
export const checkDatabaseAccess = () => {
  return checkAdminAccess('1'); // Only super admins can access database features
};

/**
 * Check if user can access user management features
 * @returns {object} - Access check result
 */
export const checkUserManagementAccess = () => {
  return checkAdminAccess('2'); // Admins and super admins can manage users
};

/**
 * Check if user can access financial features (withdrawals, payments, etc.)
 * @returns {object} - Access check result
 */
export const checkFinancialAccess = () => {
  return checkAdminAccess('1'); // Only super admins can access financial features
};

/**
 * React hook for access control
 * @param {string} requiredLevel - Required access level
 * @returns {object} - { hasAccess, loading, adminData, error }
 */
export const useAdminAccess = (requiredLevel = '1') => {
  const [accessState, setAccessState] = React.useState({
    hasAccess: false,
    loading: true,
    adminData: null,
    error: null
  });
  
  React.useEffect(() => {
    const result = checkAdminAccess(requiredLevel);
    setAccessState({
      hasAccess: result.hasAccess,
      loading: false,
      adminData: result.adminData,
      error: result.error
    });
  }, [requiredLevel]);
  
  return accessState;
};
